import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n, useT } from '../../i18n/useT'
import { lingvanexUrl } from '../../lib/lingvanex'

/** One answer names a page, so it links to it — "open the subtitle
 * converter" was an instruction to go looking. The {a}…{/a} pair marks the
 * words that become the link, the way {h}…{/h} marks the accented words in
 * hero.title, so a translator moves it with the phrase rather than with the
 * word order. It points at the tool itself (/convert), not at the English
 * landing that fronts it: the reader is already inside the app, in their own
 * language, and asked to open the thing.
 *
 * Structured data gets the same sentence with the markers taken out —
 * `stripMarkers` — because a Question/Answer is text, not markup. */
const LINKED = /^(.*)\{a\}(.*)\{\/a\}(.*)$/s

function stripMarkers(s: string): string {
  return s.replace(/\{\/?a\}/g, '')
}

function renderAnswer(answer: string) {
  const m = answer.match(LINKED)
  if (!m) return answer
  return (
    <>
      {m[1]}
      <Link to="/convert" className="text-primary hover:underline">
        {m[2]}
      </Link>
      {m[3]}
    </>
  )
}

// Display order, not catalog order: the numbering is what the translations
// are keyed on, so a question moves up the page by moving here — no string
// changes, no re-translation into 19 languages. Video sits second because it
// is what most people arrive for; the questions people ask after they have a
// result trail behind.
const FAQ_ORDER = [1, 9, 12, 2, 10, 3, 5, 7, 11, 4, 13, 6, 8]

/** Answers that name something the reader can go and look at get a link
 * under them. The third says "93 languages" and "110 languages" and gives no
 * way to see either.
 *
 * The label is the footer's own `footer.supportedLangs` — it already
 * describes this exact page in all 20 languages, and a second string saying
 * the same thing would be two places to keep in step. The flip side is that
 * editing the footer's wording silently changes this line too; that is
 * fine, they are one page, but it should be read here rather than
 * discovered in a bug report.
 *
 * Numbers are deliberately not wrapped as links themselves: the target page
 * has no anchors, so both would lead to the same place, and a link whose
 * text is "93" tells a screen reader's link list nothing. */
const ANSWER_LINK: Record<number, string> = { 3: '/language-features/' }

export function FaqSection() {
  const t = useT()
  const { locale } = useI18n()
  const [openIdx, setOpenIdx] = useState<number | null>(0)

  // Two answers tell the reader to flip the switch above the result, and the
  // switch has translated labels. Written into the sentence, they were the
  // translator's guess: ten of the twenty catalogs named a control the
  // interface does not have ("Transcript" in an Arabic UI). Filled from the
  // catalog they cannot disagree with what is on screen.
  const controls = {
    transcript: t('segment.transcript'),
    subtitles: t('segment.subtitles'),
  }
  const faq = FAQ_ORDER.map((n) => ({ n, q: t(`faq.q${n}`), a: t(`faq.a${n}`, controls) }))

  return (
    <section id="faq" className="py-16 bg-surface scroll-mt-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-text text-center">
          {t('faq.title')}
        </h2>
        <div className="mt-8 space-y-3">
          {faq.map((item, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                aria-expanded={openIdx === i}
              >
                <span className="text-sm font-medium text-text">{item.q}</span>
                <svg
                  className={`w-4 h-4 shrink-0 text-text-muted transition-transform ${openIdx === i ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIdx === i && (
                <div className="px-5 pb-4 text-sm text-text-secondary leading-relaxed">
                  <p>{renderAnswer(item.a)}</p>
                  {ANSWER_LINK[item.n] && (
                    // New tab on purpose: someone reading the FAQ while a
                    // file uploads must not be navigated out of the app
                    <a
                      href={lingvanexUrl(ANSWER_LINK[item.n], locale)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-primary hover:underline"
                    >
                      {t('footer.supportedLangs')}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faq.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: stripMarkers(item.a) },
            })),
          }),
        }}
      />
    </section>
  )
}
